import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Col, Input, notification, Row, Table, Pagination, Button } from 'antd';
import { CSVLink } from 'react-csv';
import { add, identity, isEmpty, isNil, isString, noop } from 'lodash';
import { ColumnType, TableProps } from 'antd/lib/table';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import {
  FilterOutlined,
  LeftOutlined,
  RightOutlined,
  SaveOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import './styles.css';
import { useDisplayFrameworkContext } from 'DisplayFramework/hooks/useDisplayFrameworkContext';
import Title from 'components/Title';
import ClearButton from 'components/ClearButton';
import ExtractButton from 'components/ExtractButton';
import GetLabel from 'DisplayFramework/components/GetLabel';
import { AuthContext } from 'components/Auth/Auth';
import {
  CompDisplayResponse,
  ParPagComponentRequest,
  updatePageComponent,
} from 'services/displayFramework';
import { usePaginatorItems } from 'hooks/usePaginatorItems';

export type DataGridColDef<RecordType = any> = ColumnType<RecordType> & {
  filtrable?: boolean | string;
  customfilter?: boolean;
  filtrablePrincipal?: boolean;
  filterComponent?: React.ReactElement;
  comcode?: string;
  valueFormatter?: (value: any) => any;
  children?: Array<any>;
  filterComponentStyle?: React.CSSProperties;
  spanprops?: number | string;
  width?: any;
};

export type DataGridProps<RecordType> = Pick<
  TableProps<RecordType>,
  | 'components'
  | 'dataSource'
  | 'pagination'
  | 'rowKey'
  | 'summary'
  | 'rowSelection'
  | 'expandable'
  | 'scroll'
  | 'size'
  | 'onRow'
> & {
  columns: DataGridColDef<RecordType>[];
  onSearch?: SubmitHandler<any>;
  onClear?: any;
  initialSearchValues?: any;
  extraActions?: Iterable<React.ReactNode>;
  titleComcode?: string;
  isLoading?: boolean;
  extractData?: string;
  bordered?: boolean;
  title?: any;
  header?: Iterable<React.ReactNode>;
  spanFilter?: Array<number>;
  hideFilters?: boolean;
  hideExtraAction?: boolean;
  componentName?: string;
  hideHeader?: boolean;
  disabledSearchButton?: boolean;
  hideTitle?: boolean;
  hideMarginExtractData?: boolean;
  spanprops?: number | string;
  className?: string;
  nopagination?:boolean
  scrollInput?:any;
};

const DataGrid = <RecordType extends object = any>(
  props: DataGridProps<RecordType>
) => {
  const {
    spanprops,
    bordered = false,
    columns: columnsProp,
    dataSource = [],
    extraActions,
    initialSearchValues,
    isLoading,
    disabledSearchButton,
    extractData,
    onSearch = noop,
    onClear = noop,
    summary,
    rowKey,
    rowSelection,
    expandable,
    scroll: scrollProp = {},
    scrollInput,
    size = 'middle',
    title: titleProp,
    header,
    titleComcode,
    spanFilter,
    hideFilters = false,
    hideExtraAction = false,
    componentName,
    hideHeader = false,
    hideTitle,
    hideMarginExtractData,
    className,
    nopagination,

    ...otherProps
  } = props;

  const { handleSubmit, control, reset, formState, setValue } = useForm({
    defaultValues: initialSearchValues,
    shouldUnregister: false,
  });

  const { isInspect } = useContext(AuthContext);
  const { getComponentLabel, getComponentConfiguration, pagcode, refresh } =
    useDisplayFrameworkContext();

  const handleSaveNewLabel = (comcode: string, label: string) => {
    const request: ParPagComponentRequest = {
      comcode: comcode ?? '',
      comformat: '',
      comhint: '',
      comlabel: label,
      commessage: '',
      complaceholder: '',
      comrequired: false,
      comstyle: '',
      comtype: '',
      pagcode: pagcode ?? '',
    };
    updatePageComponent({ request: request }).then((res) => {
      if (res.code === '200') {
        notification.success({
          message: res.message,
          description: res.description,
        });
        refresh();
      } else {
        notification.error({
          message: res.message,
          description: res.description,
        });
      }
    });
  };

  const columns = useMemo<ColumnType<RecordType>[]>(() => {
    return columnsProp.map(
      ({
        filtrable,
        dataIndex,
        children,
        comcode: comcodeProp,
        title,
        key,
        filterComponent,
        filterComponentStyle,
        render: renderProp,
        valueFormatter = identity,
        width,
        ...column
      }) => {
        const comcode = comcodeProp ?? (dataIndex as string) ?? (key as string);
        let label: string | any;
        let comp: CompDisplayResponse | undefined;
        comp = getComponentConfiguration(comcode);

        if (title !== undefined) {
          label = title as string;
        } else {
          label = !isEmpty(comp?.comlabel ?? '')
            ? comp?.comlabel
            : '##' + comcode;
        }

        const render: ColumnType<RecordType>['render'] = (
          value,
          record,
          index
        ) => {
          if (renderProp) {
            return renderProp(valueFormatter(value), record, index);
          } else {
            return valueFormatter(value);
          }
        };
        let newLabel = '';

        return {
          ...column,
          dataIndex,
          children,
          key,
          render,
          className: comp?.flagdisabled
            ? 'disabled-column'
            : column?.className ?? '',
          fixed: isInspect ? undefined : column?.fixed,
          width: comp?.flaghide ? 'hide' : isInspect ? 220 : width,
          title: () =>
            isInspect ? (
              <Input
                defaultValue={label}
                size="middle"
                onChange={(event) => {
                  newLabel = event.target.value;
                }}
                className="input-inspect"
                addonAfter={
                  <SaveOutlined
                    onClick={() => {
                      handleSaveNewLabel(
                        comcode,
                        newLabel === '' ? label : newLabel
                      );
                    }}
                  />
                }
              />
            ) : (
              <span>
                {comp?.flagrequired && (
                  <span
                    style={{ color: 'red', fontSize: 18, marginRight: '5px' }}
                  >
                    *
                  </span>
                )}
                <span>{label}</span>
              </span>
            ),
        };
      }
    );
  }, [isInspect ? false : columnsProp, getComponentLabel, isInspect]);

  const [showMore, setShowMore] = useState(false);

  const columnsWithFilters = useMemo(() => {
    return columnsProp.filter((column) => column.filtrable ?? false);
  }, [columnsProp]);

  const changeValuesForm = (val: any) => {
    let listnameFilter = columnsWithFilters?.map((filter) =>
      isString(filter.filtrable)
        ? String(filter.filtrable)
        : (filter.dataIndex as any)
    );
    listnameFilter?.forEach((key) => {
      setValue(key, val[key]);
    });
  };

  useEffect(() => {
    if (localStorage.getItem('searchParams_components') === componentName) {
      if (localStorage.getItem('searchParams') !== null) {
        let values: any = JSON?.parse(
          localStorage?.getItem('searchParams') ??
            JSON?.stringify(initialSearchValues) ??
            ''
        );

        changeValuesForm(values);
        setShowMore(true);
      }
    }
  }, []);

  const resetValuesForm = () => {
    onClear();
    localStorage.removeItem('searchParams');
    localStorage.removeItem('searchParams_components');
    changeValuesForm(initialSearchValues);
  };

  const title = useCallback(
    () => (
      <div className="title">
        {titleProp ? (
          <>
            <Col span={21} style={{ paddingBottom: 5 }}>
              <h3
                style={{
                  fontWeight: 'bold',
                  marginBottom: 0,
                  paddingBottom: 0,
                  fontSize: '20px',
                }}
              >
                {titleProp}
              </h3>
            </Col>
            <Col span={3} style={{ textAlign: 'right' }}>
              {hideFilters ? (hideExtraAction ? null : extraActions) : null}
            </Col>
          </>
        ) : titleComcode ? (
          titleComcode && (
            <>
              <Col span={20}>
                <Title
                  comcode={titleComcode}
                  level={4}
                  style={{ marginBottom: 0 }}
                />
              </Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                {hideFilters ? (hideExtraAction ? null : extraActions) : null}
              </Col>
            </>
          )
        ) : (
          <>
            <Col span={21} />
            <Col span={3} style={{ textAlign: 'right' }}>
              {hideFilters ? (hideExtraAction ? null : extraActions) : null}
            </Col>
          </>
        )}
        <div style={{ flexGrow: 1 }} />
      </div>
    ),
    [titleProp, hideFilters, hideExtraAction, extraActions, titleComcode]
  );
  const [state] = useState({ count: 0 });
  state.count = 0;
  const filters = useCallback(
    () => (
      <Row style={{ marginBottom: 5 }} justify="end">
        {hideFilters === true && <Col span={21} />}
        {columnsWithFilters.length >= 1 && hideFilters === false && (
          <>
            <Col
              span={hideExtraAction === true || !extraActions ? 20 : 16}
              style={{ paddingRight: 5, paddingBottom: 5 }}
            >
              <div>
                {/* <Row>
                  <Title level={4} comcode={titleComcode ?? ''} />
                </Row> */}
                <Row>
                  <Col span={24}>
                    <Row>
                      {columnsWithFilters.map((filter, index) => {
                        let span = spanFilter
                          ? spanFilter[state.count]
                          : spanprops
                          ? spanprops
                          : 6;
                        state.count = state.count + 1;
                        let newLabel = '';
                        return (
                          (index <= 3 ||
                            index <= Number(spanprops) ||
                            showMore) && (
                            <Col key={filter.key} span={span}>
                              <span style={{ display: 'block' }}>
                                {isInspect ? (
                                  <Input
                                    defaultValue={getComponentLabel(
                                      isString(filter.filtrable)
                                        ? String(filter.filtrable)
                                        : (filter.dataIndex as any)
                                    )}
                                    size="middle"
                                    onChange={(event) => {
                                      newLabel = event.target.value.concat('');
                                    }}
                                    className="input-inspect"
                                    addonAfter={
                                      <SaveOutlined
                                        onClick={() => {
                                          handleSaveNewLabel(
                                            isString(filter.filtrable)
                                              ? String(filter.filtrable)
                                              : (filter.dataIndex as any),
                                            newLabel === ''
                                              ? getComponentLabel(
                                                  isString(filter.filtrable)
                                                    ? String(filter.filtrable)
                                                    : (filter.dataIndex as any)
                                                )
                                              : newLabel
                                          );
                                        }}
                                      />
                                    }
                                  />
                                ) : (
                                  getComponentLabel(
                                    isString(filter.filtrable)
                                      ? String(filter.filtrable)
                                      : (filter.dataIndex as any)
                                  )
                                )}
                              </span>
                              {filter['filterComponent']?.props?.children
                                ?.length > 1 && !filter.customfilter ? (
                                filter['filterComponent']?.props.children?.map(
                                  (comp: any, index: number) => {
                                    return (
                                      <Controller
                                        key={index}
                                        name={
                                          !isNil(comp?.props?.name)
                                            ? String(comp?.props?.name)
                                            : ''
                                        }
                                        control={control}
                                        as={React.cloneElement(comp, {
                                          style: {
                                            width: '100%',
                                            ...filter.filterComponentStyle,
                                          },
                                        })}
                                      />
                                    );
                                  }
                                )
                              ) : (
                                <Controller
                                  name={
                                    isString(filter.filtrable)
                                      ? (filter.filtrable as string)
                                      : (filter.dataIndex as string)
                                  }
                                  control={control}
                                  as={
                                    filter['filterComponent'] ? (
                                      React.cloneElement(
                                        filter['filterComponent'],
                                        {
                                          style: {
                                            width: '100%',
                                            ...filter.filterComponentStyle,
                                          },
                                        }
                                      )
                                    ) : (
                                      <Input />
                                    )
                                  }
                                />
                              )}
                            </Col>
                          )
                        );
                      })}
                      {columnsWithFilters.length > 4 && !spanprops && (
                        <Col span={24}>
                          <Row justify="end">
                            <a
                              onClick={(ev) => {
                                ev.preventDefault();
                                if (!isInspect) setShowMore(!showMore);
                              }}
                              style={{
                                textDecoration: 'underline',
                                marginRight: '10px',
                                marginTop: '7px',
                              }}
                            >
                              <FilterOutlined />
                              <GetLabel
                                label={
                                  !showMore
                                    ? 'showAdvancedFilter'
                                    : 'hideAdvancedFilter'
                                }
                              />
                            </a>
                          </Row>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col span={4} style={{ paddingBottom: 5 }}>
              <div>
                {/* <Card type="inner" style={{ height: '100%', width: '100%' }}> */}
                {/* <SearchButton
                  htmlType="submit"
                  comcode="search"
                  disabled={isLoading || disabledSearchButton}
                  Inspectstyle={{ width: '80%' }}
                  onClick={
                    !isInspect
                      ? handleSubmit((e) => {
                        componentName &&
                          localStorage.setItem(
                            'searchParams_components',
                            componentName
                          );
                        componentName &&
                          localStorage.setItem(
                            'searchParams',
                            JSON.stringify(e)
                          );
                        onSearch(e);
                      })
                      : (null as any)
                  }
                  type="primary"
                /> */}
                <ClearButton comcode="cleara" onClick={resetValuesForm} />
              </div>
            </Col>
          </>
        )}
        {/* 
        {columnsWithFilters.length === 0 && !hideTitle && (
          <Col span={21} style={{ paddingLeft: 5, paddingBottom: 5 }}>
            {title}
          </Col>
        )} */}

        {hideFilters === false &&
        extraActions != null &&
        hideExtraAction === false ? (
          <Col span={4} style={{ paddingLeft: 5, paddingBottom: 5 }}>
            {/* <Card type="inner" style={{ height: '100%' }}> */}
            <div>
              {extraActions}
              {extractData && dataSource && dataSource.length > 0 && (
                <>
                  {!hideMarginExtractData && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                  <CSVLink
                    data={dataSource as any}
                    filename={extractData + '.csv'}
                  >
                    <ExtractButton comcode="extractData" />
                  </CSVLink>
                </>
              )}
            </div>
          </Col>
        ) : null}
      </Row>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hideFilters,
      columnsWithFilters,
      hideExtraAction,
      extraActions,
      rowSelection,
      isLoading,
      handleSubmit,
      onSearch,
      formState.isDirty,
      title,
      extractData,
      dataSource,
      spanFilter,
      state,
      getComponentLabel,
      control,
      reset,
      showMore,
      hideHeader,
      isInspect,
      resetValuesForm,
      changeValuesForm,
      disabledSearchButton,
    ]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [dataSource]);

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); 
    }
  };

  const handlePageSizeChange = (e: any) => {
    const newPageSize = parseInt(e.target.value);
    setPageSize(newPageSize);
    setCurrentPage(1); 
  };

  const paginatedDataSource = dataSource.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // const CustomPagination = () => {
  //   const totalPages = Math.ceil(dataSource.length / pageSize);

  //   return (
  //     <div style={{ display: 'flex', float: 'right', alignItems: 'center' }}>
  //       <span className="mt-1">Items per page: </span>
  //       <select
  //         style={{ height: '30px' }}
  //         name="itemperpage"
  //         className=" mr-3 ml-3"
  //         value={pageSize}
  //         onChange={handlePageSizeChange}
  //       >
  //         <option value="1">1</option>
  //         <option value="10">10</option>
  //         <option value="20">20</option>
  //         <option value="30">30</option>
  //       </select>

  //       <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
  //         <Button
  //           type="link"
  //           icon={<VerticalRightOutlined />}
  //           disabled={currentPage === 1}
  //           onClick={() => handlePageChange(1, pageSize)}
  //           className='no-shadow'
  //         />

  //         <Button
  //           type="link"
  //           icon={<LeftOutlined />}
  //           disabled={currentPage === 1}
  //           onClick={() => handlePageChange(currentPage - 1, pageSize)}
  //           className='no-shadow'

  //         />

  //         <Button
  //           type="link"
  //           icon={<RightOutlined />}
  //           disabled={currentPage === totalPages}
  //           onClick={() => handlePageChange(currentPage + 1, pageSize)}
  //           className='no-shadow'

  //         />

  //         <Button
  //           type="link"
  //           icon={<VerticalLeftOutlined />}
  //           disabled={currentPage === totalPages}
  //           onClick={() => handlePageChange(totalPages, pageSize)}
  //           className='no-shadow'

  //         />
  //       </div>
  //     </div>
  //   );
  // };

  return (
    <Table
      showHeader={!hideHeader}
      sortDirections={['ascend']}
      expandable={expandable}
      columns={columns.filter((e) => e.width !== 'hide')}
      dataSource={dataSource}
      loading={isLoading}
      rowKey={rowKey}
      summary={summary}
      className="DataGridSFS"
      rowSelection={rowSelection}
      size={size}
      title={!hideTitle && props.hideFilters ? title : undefined}
      bordered={bordered}
      {...otherProps}
      rowClassName={(record, index) =>
        index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
      }
      // footer={nopagination ? undefined : CustomPagination}
      // pagination={false} // Disable built-in pagination
      scroll={scrollInput}
    />
  );
};

export default DataGrid;

const DEFAULT_COLUMN_WIDTH = 100;
const getColumnWidth = <RecordType extends {}>({
  width,
}: ColumnType<RecordType>) => {
  if (width) {
    if (typeof width === 'number') {
      return width;
    } else {
      return Number(width.replace('px', ''));
    }
  } else {
    return DEFAULT_COLUMN_WIDTH;
  }
};
